import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { DurationIcon, LightbulbIcon, CheckCircleFilledIcon } from '@/src/renderer/components/icons'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import { Button } from '@/src/renderer/components/ui/Button'
import { formatFocusAgainstEstimate, isOverEstimate } from '@/src/renderer/lib/focus'
import { escapeBelongsToTaskDetails } from '../../utils/inlineEscape'
import { parseDayDuration, parseDuration, MINUTES_PER_DAY } from '../../utils/parseDuration'
import type { DurationMode, DurationValue } from '../TaskEntry'
import type { DurationDropdownContentProps } from './DurationDropdown.types'
import './DurationDropdown.scss'

const MINUTE_OPTIONS: { minutes: number; labelKey: string }[] = [
  { minutes: 5, labelKey: 'taskCreation.duration5min' },
  { minutes: 10, labelKey: 'taskCreation.duration10min' },
  { minutes: 15, labelKey: 'taskCreation.duration15min' },
  { minutes: 30, labelKey: 'taskCreation.duration30min' },
  { minutes: 45, labelKey: 'taskCreation.duration45min' },
]

const HOUR_OPTIONS: { minutes: number; labelKey: string; mode?: DurationMode }[] = [
  { minutes: 60, labelKey: 'taskCreation.duration1h' },
  { minutes: 120, labelKey: 'taskCreation.duration2h' },
  { minutes: 180, labelKey: 'taskCreation.duration3h' },
  { minutes: 240, labelKey: 'taskCreation.durationHalfDay' },
  // Wer „Ganzer Tag“ wählt, meint einen Tag: Die Marke zeigt „1t“, nicht „8h“ (#197).
  { minutes: MINUTES_PER_DAY, labelKey: 'taskCreation.durationFullDay', mode: 'days' },
]

const THINKING_MINUTES = 10

/**
 * Content for the duration dropdown menu.
 * Renders inside a DropdownMenuContent managed by TaskActionButton.
 *
 * Features:
 * - Search/filter input with natural language parsing (10m, 2h, 1:30, 3 Tage)
 * - Two-column layout: Minutes (1-5) and Hours (6-8)
 * - "10 Min zum Durchdenken" special option (T)
 * - Keyboard shortcut T for thinking mode
 */
export const DurationDropdownContent: React.FC<DurationDropdownContentProps> = ({ value, onValueChange, onClose, onRemove, placement = 'actionbar', focusedSeconds }) => {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const inline = placement === 'inline'

  /*
    **Was verbraucht ist** — „9 Min 53 Sek von 10 Min" (Nutzer-Vorgabe,
    2026-09-18).

    In Worten und nicht als Uhr: `0:09m` zwang zum Umrechnen, bevor man
    begriff, dass neun Sekunden gemeint sind. Erst neben der veranschlagten
    Dauer wird aus der gelaufenen Zeit überhaupt eine Auskunft.

    Über der Dauer färbt sich die Angabe um und sagt sonst nichts — dieselbe
    stille Regel wie beim Chip der Pille (CONTEXT.md, Überzeit).
  */
  const verbraucht = (typeof focusedSeconds === 'number' && focusedSeconds > 0)
    ? {
        text: formatFocusAgainstEstimate(focusedSeconds, value?.minutes ?? null, t),
        ueber: isOverEstimate(focusedSeconds, value?.minutes ?? null),
      }
    : null

  const handleSelect = useCallback((val: DurationValue) => {
    onValueChange(val)
    onClose()
  }, [onValueChange, onClose])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // In den Aufgaben-Details gehört Escape der Spalte — vor dem
    // `stopPropagation` hinauslassen.
    if (escapeBelongsToTaskDetails(e, inline)) return
    // Always stop propagation so Radix typeahead/navigation doesn't steal focus.
    e.stopPropagation()

    if (e.key === 'Enter') {
      e.preventDefault()
      const days = parseDayDuration(inputValue)
      if (days !== null) {
        handleSelect({ minutes: days * MINUTES_PER_DAY, mode: 'days' })
        return
      }
      const parsed = parseDuration(inputValue)
      if (parsed !== null && parsed > 0) {
        handleSelect({ minutes: parsed, mode: 'duration' })
      }
      return
    }

    if (e.key === 't' || e.key === 'T') {
      // Only trigger thinking shortcut when input is empty.
      if (inputValue === '') {
        e.preventDefault()
        handleSelect({ minutes: THINKING_MINUTES, mode: 'thinking' })
      }
    }
  }, [handleSelect, inline, inputValue])

  // Tage und Minuten sind dieselbe Dauer, nur anders geschrieben: Eine Aufgabe, die
  // vor #197 „Ganzer Tag“ als 480 Min. bekam, trägt den Haken weiter dort.
  const isSelected = (minutes: number, mode: DurationMode) =>
    value?.minutes === minutes && (value.mode === 'thinking') === (mode === 'thinking')

  return (
    <div className="duration-dropdown" data-placement={placement}>
      <div className="duration-dropdown__search-wrapper">
        <div className="duration-dropdown__search">
          <DurationIcon size={16} />
          <input
            ref={inputRef}
            className="duration-dropdown__input"
            placeholder={t('taskCreation.durationPlaceholder')}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            // In den Aufgaben-Details stehen sieben Inhalte gleichzeitig offen — sechs
            // von ihnen hätten ein `autoFocus`, und der letzte gewänne. Der Fokus gehört
            // dort der Liste (CONTEXT.md, Aufgaben-Details), bis jemand hineingreift.
            autoFocus={!inline}
          />
        </div>
        {/* AUSSERHALB des Eingabefelds (Nutzer-Vorgabe): Sie ist zu lesen,
            nicht zu tippen — im Kasten sah sie aus wie ein Teil der Eingabe. */}
        {verbraucht && (
          <p className="duration-dropdown__spent" data-over={verbraucht.ueber ? '' : undefined}>
            {verbraucht.text}
          </p>
        )}
      </div>

      <div className="duration-dropdown__selection">
        <div className="duration-dropdown__columns">
          {/* Minutes column */}
          <div className="duration-dropdown__column">
            <div className="duration-dropdown__column-label">
              {t('taskCreation.durationMinutes')}
            </div>
            {MINUTE_OPTIONS.map(option => (
              <button
                key={option.minutes}
                type="button"
                className={`context-menu-content__item duration-dropdown__item${isSelected(option.minutes, 'duration') ? ' duration-dropdown__item--selected' : ''}`}
                onClick={() => handleSelect({ minutes: option.minutes, mode: 'duration' })}
              >
                {t(option.labelKey)}
                {isSelected(option.minutes, 'duration') && (
                  <div className="duration-dropdown__item-right">
                    <CheckCircleFilledIcon size={16} className="duration-dropdown__check" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Vertical separator */}
          <div className="duration-dropdown__separator-vertical" />

          {/* Hours column */}
          <div className="duration-dropdown__column">
            <div className="duration-dropdown__column-label">
              {t('taskCreation.durationHours')}
            </div>
            {HOUR_OPTIONS.map(option => (
              <button
                key={option.minutes}
                type="button"
                className={`context-menu-content__item duration-dropdown__item${isSelected(option.minutes, 'duration') ? ' duration-dropdown__item--selected' : ''}`}
                onClick={() => handleSelect({ minutes: option.minutes, mode: option.mode ?? 'duration' })}
              >
                {t(option.labelKey)}
                {isSelected(option.minutes, 'duration') && (
                  <div className="duration-dropdown__item-right">
                    <CheckCircleFilledIcon size={16} className="duration-dropdown__check" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="context-menu-content__separator" />

        {/* Thinking option */}
        <button
          type="button"
          className={`context-menu-content__item duration-dropdown__item duration-dropdown__item--thinking${isSelected(THINKING_MINUTES, 'thinking') ? ' duration-dropdown__item--selected' : ''}`}
          onClick={() => handleSelect({ minutes: THINKING_MINUTES, mode: 'thinking' })}
        >
          <LightbulbIcon size={16} />
          {t('taskCreation.durationThinking')}
          <div className="duration-dropdown__item-right">
            {isSelected(THINKING_MINUTES, 'thinking') && (
              <CheckCircleFilledIcon size={16} className="duration-dropdown__check" />
            )}
            <ShortcutHint>T</ShortcutHint>
          </div>
        </button>
      </div>

      {/*
        **In den Aufgaben-Details: ein Fuß wie bei Wann und Deadline** (Etappe 3,
        2026-09-19) — Trenner, darunter „Entfernen" links. Bis dahin stand dort
        ein kleiner Papierkorb rechts im Gruppentitel; da war er falsch: Er
        gehört zum Wert, nicht zur Überschrift.

        Der Griff sagt es, nicht der Einbauort: Der Aufrufer reicht `onRemove`
        erst, wenn eine Dauer dasteht — ohne Wert also gar kein Fuß (ADR 0026).
        Kein `onClose`: In den Details steht der Kasten offen.
      */}
      {placement === 'inline' && onRemove && (
        <div className="duration-dropdown__footer">
          <div className="context-menu-content__separator" />
          <div className="duration-dropdown__footer-actions">
            <Button variant="outline" icon={<Trash2 size={16} />} onClick={onRemove}>
              {t('taskCreation.durationRemove')}
            </Button>
          </div>
        </div>
      )}

      {/* An der Marke wie im Aufgaben-Menü (#216) — sobald der Aufrufer den Griff reicht. */}
      {(placement === 'badge' || placement === 'actionbar') && onRemove && (
        <div className="duration-dropdown__remove-section">
          <div className="context-menu-content__separator" />
          <div className="duration-dropdown__remove-wrapper">
            <Button variant="outline" icon={<Trash2 size={16} />} onClick={onRemove}>
              {t('taskCreation.durationRemove')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
